import { styled } from '@mui/system';
import { useEventBus } from '../../../../../hooks/use-event-bus';
import { Avatar } from '@revenuewell/ui-icons';
import { IContactProfile } from '../../../../../services/api-client/interface';
import moment from 'moment';
import { EventMessengerWidget, useConfig, useOidcWithAdmin, useProduct } from '@revenuewell/front-end-bundle';
import { EventJumpToMessage } from '../../../../../types/event-bus';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ChannelSearch } from '../../../../../services/inbox-service/types';
import { usePubnub } from '../../../../../hooks/use-pubnub/use-pubnub';
import InboxService from '../../../../../services/inbox-service/inbox-service';
import { useEffect, useState } from 'react';
import { formatUSPhoneNumber } from '../../../../../utils/formatter';

const Container = styled(Box)({
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '12px 0px',
  borderBottom: '1px solid lightgrey',
  margin: '0px 20px'
});

const AvatarContainer = styled('div')({
  paddingLeft: '10px'
});

const ContactInfoContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '0px 10px'
});

const NameLabel = styled('label')({
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontStyle: 'normal',
  fontSize: 16,
  fontWeight: 500,
  lineHeight: '28px',
  color: '#000',
  padding: '0px 12px'
});

const SubTextContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  padding: '0px 12px'
});

const SubText = styled(Typography)({
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  fontStyle: 'normal',
  fontSize: 12,
  fontWeight: 400,
  lineHeight: '28px',
  color: '#565F6F'
});

const InvalidPhone = styled('span')({
  color: '#E25650'
});

interface FindPatientCardProps {
  contactInfo: IContactProfile;
}

export default function FindPatientCard({ contactInfo }: FindPatientCardProps) {
  const { publish } = useEventBus();
  const { hasProduct } = useProduct();
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { mailboxes } = usePubnub();
  const [recentComm, setRecentComm] = useState<ChannelSearch>();

  const renderPhone = () => {
    if (contactInfo.cellPhone) return formatUSPhoneNumber(contactInfo.cellPhone);
    else return <InvalidPhone>no cell phone</InvalidPhone>;
  };

  const fetchRecentComms = async (contactInfo: IContactProfile) => {
    const phoneNumber = contactInfo.cellPhone || contactInfo.homePhone || contactInfo.workPhone;
    if (!phoneNumber) return;

    const currentMailbox = mailboxes.find(mailbox => mailbox.locationId === contactInfo.location?.id);

    if (!currentMailbox) return;

    const searchRequest = {
      mailboxIds: [currentMailbox.id],
      phoneNumbers: [phoneNumber]
    };

    const inboxService = InboxService.getInstance(config, oidcService);
    const inboxResponse = await inboxService.channelSearch(searchRequest);

    setRecentComm(inboxResponse[0]);
  };

  const getLastContactedDate = () => {
    if (!recentComm) return;

    let mostRecent = '';
    if (recentComm.lastCall && recentComm.lastMessage) {
      if (recentComm.lastCall.timeToken > recentComm.lastMessage.timeToken) {
        mostRecent = 'lastCall';
      } else {
        mostRecent = 'lastMessage';
      }
    }

    if ((recentComm.lastMessage && mostRecent === '') || (recentComm.lastMessage && mostRecent === 'lastMessage')) {
      return recentComm.lastMessage.date;
    }

    if ((recentComm.lastCall && mostRecent === '') || (recentComm.lastCall && mostRecent === 'lastCall')) {
      return recentComm.lastCall.date;
    }
  };

  const handleMessageClick = () => {
    if (!contactInfo.cellPhone) return;

    setTimeout(() => {
      if (hasProduct('messenger')) {
        publish({
          messageType: 'jumpToMessage',
          data: {
            patientId: contactInfo.id
          } as EventJumpToMessage['data']
        });
      } else {
        publish({
          messageType: 'messengerWidget',
          data: {
            phoneNumber: contactInfo.cellPhone,
            locationId: contactInfo.location.id
          } as EventMessengerWidget['data']
        });
      }
    }, 1000);
  };

  useEffect(() => {
    fetchRecentComms(contactInfo);
  }, []);

  return (
    <Container
      onClick={handleMessageClick}
      sx={{
        opacity: !contactInfo.cellPhone ? 0.4 : 1,
        '&:hover': {
          backgroundColor: !contactInfo.cellPhone ? 'inherited' : '#f0f1f4'
        },
        '&:active': {
          backgroundColor: '#fff'
        }
      }}
    >
      <AvatarContainer>
        <Avatar size='l' />
      </AvatarContainer>

      <ContactInfoContainer>
        <NameLabel>{`${contactInfo.firstName} ${contactInfo.lastName}`}</NameLabel>
        <SubTextContainer>
          <SubText variant='caption'>
            {contactInfo.dateOfBirth && (
              <>
                {moment(contactInfo.dateOfBirth).format('MM/DD/YYYY')}
                <Box component='span' m={1} display='inline'>
                  |
                </Box>
              </>
            )}
            {renderPhone()}
          </SubText>
          {recentComm && (
            <SubText variant='caption'>Last contacted: {moment(getLastContactedDate()).format('MM/DD/YYYY')}</SubText>
          )}
        </SubTextContainer>
      </ContactInfoContainer>
    </Container>
  );
}
